<template>
  <div class="page-view">

    <BasePageHeader :title="$t('priceGroups.title')" @back="$router.back()">
      <template #actions>
        <router-link to="/bulk-edit/priceGroups" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button class="btn primary sm" @click="openCreate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="px-4 pt-3.5 pb-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('priceGroups.searchPlaceholder')" />
    </div>

    <div v-if="formOpen" class="mx-4 my-2 p-4 rounded-xl flex flex-col gap-2.5 border border-[var(--color-border)]" style="background:var(--color-surface)">
      <div class="text-sm font-semibold">{{ editing ? $t('priceGroups.editFormTitle') : $t('priceGroups.newFormTitle') }}</div>
      <BaseFormField :label="$t('common.name')" required>
        <BaseInput v-model="form.name" :placeholder="$t('locations.namePlaceholder')" />
      </BaseFormField>
      <BaseFormField :label="$t('common.description')">
        <BaseInput v-model="form.description" :placeholder="$t('common.description')" />
      </BaseFormField>
      <BaseFormField :label="$t('priceGroups.defaultDiscountLabel')">
        <BaseInput v-model="form.default_discount_percentage" class="w-[100px]" placeholder="0.00" type="number" min="0" max="100" step="0.01" />
      </BaseFormField>
      <BaseFormActions
        :confirm-label="editing ? $t('common.update') : $t('common.create')"
        :loading="saving"
        :disabled="!form.name"
        @cancel="closeForm"
        @confirm="save"
      />
    </div>

    <div v-if="loading" class="state-empty">{{ $t('common.loading') }}</div>
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>
    <div v-else-if="!filtered.length" class="state-empty">
      {{ query ? $t('priceGroups.noMatch') : $t('priceGroups.noPriceGroups') }}
    </div>

    <div v-else class="list-stack">
      <div v-for="pg in filtered" :key="pg.id" class="data-card" style="cursor:pointer" @click="router.push(`/price-groups/${pg.id}`)">
        <div class="data-card__main">
          <div class="data-card__name">{{ pg.name }}</div>
          <div class="data-card__meta">
            <span v-if="pg.default_discount_percentage != null" class="badge" style="background:#2d2a1e;color:#fcd34d">
              {{ $t('priceGroups.discountBadge', { pct: pg.default_discount_percentage }) }}
            </span>
            <span v-if="pg.items_count" class="badge" style="background:#1e3a5f;color:#93c5fd">{{ $t('priceGroups.itemsCount', { n: pg.items_count }) }}</span>
            <span v-if="pg.description" class="text-xs text-[var(--color-muted)] mt-1">{{ pg.description }}</span>
          </div>
        </div>
        <div class="data-card__actions" @click.stop>
          <BaseIconButton :title="$t('common.edit')" @click="openEdit(pg)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
          </BaseIconButton>
          <BaseIconButton variant="danger" :title="$t('common.delete')" @click="remove(pg)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
          </BaseIconButton>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormActions from '@/components/base/BaseFormActions.vue'
import BaseIconButton from '@/components/base/BaseIconButton.vue'
import { getPriceGroups, createPriceGroup, updatePriceGroup, deletePriceGroup } from '@/api/api.js'
import SearchField from '@/components/search/SearchField.vue'

const router = useRouter()
const { t } = useI18n()
const toast = useToast()
const query = ref('')
const priceGroups = ref([])
const loading = ref(false)
const loadError = ref(null)
const formOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const form = ref({ name: '', description: '', default_discount_percentage: '' })

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return priceGroups.value
  return priceGroups.value.filter(pg =>
    pg.name?.toLowerCase().includes(q) ||
    pg.description?.toLowerCase().includes(q)
  )
})

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const res = await getPriceGroups()
    priceGroups.value = res.results ?? res ?? []
  } catch {
    loadError.value = t('priceGroups.loadFailed')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = null
  form.value = { name: '', description: '', default_discount_percentage: '' }
  formOpen.value = true
}

function openEdit(pg) {
  editing.value = pg
  form.value = {
    name: pg.name,
    description: pg.description ?? '',
    default_discount_percentage: pg.default_discount_percentage ?? '',
  }
  formOpen.value = true
}

function closeForm() {
  formOpen.value = false
  editing.value = null
}

async function save() {
  saving.value = true
  try {
    const payload = { name: form.value.name, description: form.value.description }
    if (form.value.default_discount_percentage !== '') {
      payload.default_discount_percentage = form.value.default_discount_percentage
    }
    if (editing.value) {
      const updated = await updatePriceGroup(editing.value.id, payload)
      const idx = priceGroups.value.findIndex(pg => pg.id === editing.value.id)
      if (idx !== -1) priceGroups.value[idx] = updated
      toast.success(t('priceGroups.updated'))
    } else {
      const created = await createPriceGroup(payload)
      priceGroups.value.unshift(created)
      toast.success(t('priceGroups.created'))
    }
    closeForm()
  } catch {
    toast.error(t('priceGroups.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function remove(pg) {
  if (!confirm(t('priceGroups.deleteConfirm', { name: pg.name }))) return
  try {
    await deletePriceGroup(pg.id)
    priceGroups.value = priceGroups.value.filter(x => x.id !== pg.id)
    toast.success(t('priceGroups.deleted'))
  } catch {
    toast.error(t('priceGroups.saveFailed'))
  }
}

onMounted(load)
</script>


